import React, { useEffect, useState } from "react";
import "./VisionMission.css";
import ChairmanProfileModal from "./ChairmanProfileModal";

const PUBLIC = process.env.PUBLIC_URL || "";

const API_BASE =
    process.env.REACT_APP_API_BASE ??
    (process.env.NODE_ENV === "production" ? "" : "http://localhost:5000");

/* ===============================
   FALLBACK DATA
   =============================== */
const fallbackData = [
    { primaryid: 1, firstname: "Alan", lastname: "Cruz", yearserved: "2007-2009" },
    { primaryid: 2, firstname: "Donnie", lastname: "Arteta", yearserved: "2010" },
    { primaryid: 3, firstname: "Geraldine", lastname: "Empic", yearserved: "2011" },
    { primaryid: 4, firstname: "Vance", lastname: "Nicholas", yearserved: "2012" },
    { primaryid: 5, firstname: "Toffey", lastname: "Perlas", yearserved: "2013-2014" },
    { primaryid: 6, firstname: "Iren", lastname: "Monge", yearserved: "2015" },
    { primaryid: 7, firstname: "Marloun", lastname: "Manuel", yearserved: "2016-2017" },
    { primaryid: 8, firstname: "Raymond", lastname: "Gregorio", yearserved: "2018-2019" },
    { primaryid: 9, firstname: "Ernesto", lastname: "Morandarte", yearserved: "2020-2021" },
    { primaryid: 10, firstname: "Edwin Jr.", lastname: "Ramirez", yearserved: "2022" },
    { primaryid: 11, firstname: "Mon", lastname: "Sales", yearserved: "2023" },
    { primaryid: 12, firstname: "Marty", lastname: "Almarez", yearserved: "2024" },
    { primaryid: 13, firstname: "Reynaldo Jr.", lastname: "Arcenas", yearserved: "2025" },
    { primaryid: 14, firstname: "John Regis", lastname: "Urquico", yearserved: "2026" }
];

export default function VisionMission() {
    const [legacyList, setLegacyList] = useState([]);
    const [activeIndex, setActiveIndex] = useState(null);
    const [loading, setLoading] = useState(true);

    /* ===============================
       LOAD DATA FUNCTION
       =============================== */
    const loadData = () => {
        setLoading(true); // ⏳ start loading

        fetch(`${API_BASE}/api/legacy/public`)
            .then((res) => res.json())
            .then((data) => {
                if (data.ok && data.rows.length > 0) {
                    setLegacyList(data.rows);

                    // Save cache
                    localStorage.setItem(
                        "chairman_cache",
                        JSON.stringify(data.rows)
                    );
                } else {
                    throw new Error("Empty data");
                }
            })
            .catch(() => {
                const cache = localStorage.getItem("chairman_cache");

                if (cache) {
                    setLegacyList(JSON.parse(cache));
                } else {
                    setLegacyList(fallbackData);
                }
            })
            .finally(() => {
                setLoading(false); // ✅ stop loading
            });
    };

    /* ===============================
       INITIAL LOAD
       =============================== */
    useEffect(() => {
        loadData();
    }, []);

    /* ===============================
       🔄 AUTO-REFRESH EVERY 10 SECONDS
       =============================== */
    useEffect(() => {
        const interval = setInterval(() => {
            loadData();
        }, 300000); // every 5 mins

        return () => clearInterval(interval);
    }, []);

    /* ===============================
       SCROLL ANIMATION
       =============================== */
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) =>
                entries.forEach((entry) =>
                    entry.target.classList.toggle("animate", entry.isIntersecting)
                ),
            { threshold: 0.2 }
        );

        const els = document.querySelectorAll(".fade-on-scroll");
        els.forEach((el) => observer.observe(el));
        return () => els.forEach((el) => observer.unobserve(el));
    }, []);

    const midpoint = Math.ceil(legacyList.length / 2);
    const colA = legacyList.slice(0, midpoint);
    const colB = legacyList.slice(midpoint);

    return (
        <>
            {loading && (
                <div className="loader"></div>
            )}

            {/* ===============================
               CNC SECTION
               =============================== */}
            <div
                className="vision-mission-bg"
                style={{
                    "--vision-bg": `url("${PUBLIC}/larawan/TGPLogoLigthning2.png")`,
                    "--vision-mobile-bg": `url("${PUBLIC}/larawan/TGPLogoMobile2.png")`,
                }}
            >
                <section className="cnc-leadership fade-on-scroll">
                    <h2 className="cnc-title">Southern Alberta Regional Council</h2>
                    <p className="cnc-text">
                        The Southern Alberta Regional Council (SARC), based in Calgary, Alberta,
                        operates under the authority of the Canada National Council. The Council
                        is led by duly elected Chairmen and Chairwomen who provide leadership,
                        governance, and continuity in alignment with the principles and standards
                        of Tau Gamma Phi and Tau Gamma Sigma.
                    </p>
                    <h3 className="cnc-subtitle">
                        Chairmen and Chairwomen of the Southern Alberta Regional Council
                    </h3>

                    <div className="cnc-two-column">
                        <ul>
                            {colA.map((m, i) => (
                                <li
                                    key={`${m.primaryid}-${m.yearserved}`}
                                    className="chairman-name"
                                    onClick={() => setActiveIndex(i)}
                                >
                                    {m.firstname} {m.lastname} ({m.yearserved})
                                </li>
                            ))}
                        </ul>

                        <ul>
                            {colB.map((m, i) => (
                                <li
                                    key={`${m.primaryid}-${m.yearserved}`}
                                    className="chairman-name"
                                    onClick={() => setActiveIndex(i + midpoint)}
                                >
                                    {m.firstname} {m.lastname} ({m.yearserved})
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                {/* ===============================
			       VISION & MISSION SECTION
			       =============================== */}
                <section id="mission" className="vision-mission">
                    <h2 className="header2 fade-on-scroll">
                        Southern Alberta Regional Council Vision &amp; Mission
                    </h2>

                    <div className="section-two fade-on-scroll">
                        <h3>Vision</h3>
                        <p>
                            To be a strong and united brotherhood of Triskelions who excel in all
                            fields of human endeavor, guided by the principles of integrity,
                            discipline, and equality, and committed to serving humanity with honor
                            and pride.
                        </p>

                        <h3>Mission</h3>
                        <p>
                            To uphold the Tenets and Codes of Conduct of the fraternity, strengthen
                            the bond of brotherhood, and develop men of character and leadership.
                            We commit ourselves to service, excellence, and the principle that
                            Man is Brother unto Man, living as true Triskelions who lead, serve,
                            and inspire others.
                        </p>
                    </div>
                    {/* Hidden by request. Restore this image block if the logo is needed again.
                    <img
                        className="imaagelogo fade-on-scroll"
                        src={`${PUBLIC}/images/Triskelion.png`}
                        alt="Vision & Mission"
                    />
                    */}
                </section>
            </div>

            {/* ===============================
               MODAL
               =============================== */}
            {activeIndex !== null && (
                <ChairmanProfileModal
                    list={legacyList}
                    index={activeIndex}
                    onClose={() => setActiveIndex(null)}
                    onPrev={() => setActiveIndex((i) => i - 1)}
                    onNext={() => setActiveIndex((i) => i + 1)}
                />
            )}
        </>
    );
}
